import classNames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { CSSTransitionClassNames } from 'react-transition-group/CSSTransition';

import { useUIMode } from '../../contexts/UIModeContext';
import { ReactComponent as CheckmarkIcon } from '../../images/icons/checkmark.svg';
import { UnlockDeviceType } from '../../util/unlockMethods';

import { ReactComponent as KeyIcon } from './key.svg';
import { ReactComponent as MobileIcon } from './mobile.svg';
/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['connecting', 'connected', 'confirmed', 'warning', 'error'] }] */
import styles from './PushState.module.css';
import { ReactComponent as SecurityKeyIcon } from './security-key.svg';

export type PushStateType = 'connecting' | 'connected' | 'confirmed' | 'warning' | 'error';

interface Props {
  className?: string;
  state: PushStateType;
  small?: boolean;
  unlockDeviceType: UnlockDeviceType;
}

export default function PushState({ className, state, small, unlockDeviceType }: Props) {
  // allow running animation to finish before
  const root = useRef<HTMLDivElement | null>(null);
  const rotating = useAnimationEnd(root, state === 'connecting');
  const isConfirmed = state === 'confirmed';

  const fadeClassNames: CSSTransitionClassNames = {
    enter: styles.fadeEnter,
    enterActive: styles.fadeEnterActive,
    exit: styles.fadeExit,
    exitActive: styles.fadeExitActive,
  };

  const Icon =
    isConfirmed ? CheckmarkIcon
    : unlockDeviceType === 'multiple' || unlockDeviceType === 'mobileSafari' ? KeyIcon
    : unlockDeviceType === 'securityKey' ? SecurityKeyIcon
    : MobileIcon;

  const iconWrapperRef = useRef<HTMLSpanElement | null>(null);

  const uiMode = useUIMode();

  return (
    <div
      ref={root}
      className={classNames(
        styles.PushState,
        styles[state],
        {
          [styles.rotating]: rotating,
          [styles.small]: small,
          [styles.light]: uiMode === 'light',
        },
        className,
      )}
    >
      <SwitchTransition>
        <CSSTransition
          key={isConfirmed ? 'a' : 'b'}
          addEndListener={(done) =>
            iconWrapperRef.current?.addEventListener('transitionend', done, false)
          }
          classNames={fadeClassNames}
          nodeRef={iconWrapperRef}
        >
          <span className={styles.IconWrapper} ref={iconWrapperRef}>
            <Icon className={styles.Icon} />
          </span>
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
}

/**
 * Lets the running animation finish its current iteration before stopping it.
 */
function useAnimationEnd(root: React.MutableRefObject<HTMLElement | null>, shouldAnimate: boolean) {
  const [animating, setAnimating] = useState(shouldAnimate);
  useEffect(() => {
    const el = root?.current;
    function handleAnimationOnIteration() {
      setAnimating(false);
      el?.removeEventListener('animationiteration', handleAnimationOnIteration);
    }

    if (shouldAnimate) {
      setAnimating(true);
    } else {
      el?.addEventListener('animationiteration', handleAnimationOnIteration);
    }

    return () => {
      el?.removeEventListener('animationiteration', handleAnimationOnIteration);
    };
  }, [root, shouldAnimate]);

  return animating;
}
